import type { Size } from '../geometry/framing';
import type { CaptionSettings } from './types';

export interface CaptionRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Where the preset positions put captions, as shares of the frame's height:
 * a top caption starts at `top`, a bottom caption's last line ends at
 * `bottom`. A video fitted for a place moves them clear of what the place
 * draws over the picture (`placedCaptions` in src/core/project/placement.ts).
 */
export interface CaptionLines {
  readonly top: number;
  readonly bottom: number;
}

export const DEFAULT_CAPTION_LINES: CaptionLines = { top: 0.12, bottom: 0.8 };

/** Captions keep this much distance from the frame edge (share of the short side). */
const EDGE_MARGIN = 0.03;
/** A dragged caption snaps to the horizontal centre when it gets this close (share of the width). */
export const CENTER_SNAP = 0.025;

const clamp = (value: number, min: number, max: number) => (max < min ? (min + max) / 2 : Math.min(max, Math.max(min, value)));

/**
 * Where a caption block of `block` size sits on the output frame: at the
 * dragged anchor when there is one (kept inside the frame: a place the person
 * chose wins), otherwise at the preset position on its lines. Shared by the preview, the export and drag hit-testing.
 */
export function placeCaption(frame: Size, block: { readonly width: number; readonly height: number }, settings: Pick<CaptionSettings, 'position' | 'anchor' | 'lines'>): CaptionRect {
  const { width, height } = block;
  if (settings.anchor) {
    const margin = Math.min(frame.width, frame.height) * EDGE_MARGIN;
    return {
      x: clamp(settings.anchor.x * frame.width - width / 2, margin, frame.width - margin - width),
      y: clamp(settings.anchor.y * frame.height - height / 2, margin, frame.height - margin - height),
      width,
      height,
    };
  }
  const lines = settings.lines ?? DEFAULT_CAPTION_LINES;
  const y = settings.position === 'top' ? frame.height * lines.top : settings.position === 'middle' ? (frame.height - height) / 2 : frame.height * lines.bottom - height;
  return { x: (frame.width - width) / 2, y, width, height };
}

/**
 * The anchor (block centre, 0..1 of the frame) after dragging a caption whose
 * centre started at `start` by `delta` (output pixels). Snaps to the centre line.
 */
export function dragAnchor(frame: Size, start: { readonly x: number; readonly y: number }, delta: { readonly x: number; readonly y: number }): { x: number; y: number; snapped: boolean } {
  const x = clamp((start.x + delta.x) / frame.width, 0, 1);
  const y = clamp((start.y + delta.y) / frame.height, 0, 1);
  const snapped = Math.abs(x - 0.5) < CENTER_SNAP;
  return { x: snapped ? 0.5 : x, y, snapped };
}
